/**
 * Encode / decode the single reply the Review card sends for a group of items.
 * Each item is a numbered heading (what was asked) followed, after a blank line,
 * by its answer, so the question and the answer read as two separate things, in
 * the order the agent raised them:
 *
 *     My answers
 *
 *     1. Question: Which encounter sets the index date?
 *
 *        Answer: First occurrence where all other criteria are also met
 *
 * The blank line keeps them on separate lines when the reply is rendered as
 * Markdown, and the answer is indented by the width of its marker ("1. " is 3
 * columns, "10. " is 4) so Markdown keeps it inside its item: a shorter indent
 * drops it out of the list from the tenth item on. Reading the same text back
 * (live, replay, second tab) rebuilds the resolved card from the answer alone.
 */

import { matchRequestedLabel, matchVerdict, parseMatchVerdict } from './variableMatchAnswer';
import type { ReviewItem, ReviewPick } from '../types/review-types';

const REPLY_PREAMBLE = 'My answers';
const ANSWER_LABEL = 'Answer';
const ITEM_SEPARATOR = '\n\n';
const UNANSWERED_PHRASE = 'No answer given.';

/**
 * Whether a message is a combined reply written by {@link buildReviewReply}, so a
 * chat bubble can render the Markdown in the questions it repeats.
 */
export const isReviewReplyText = (text: string): boolean =>
  text.startsWith(`${REPLY_PREAMBLE}${ITEM_SEPARATOR}1. `) &&
  new RegExp(`\\n\\n {3,}${ANSWER_LABEL}: `).test(text);

/**
 * One line, so each heading and answer stays a single line: text that spans lines
 * (or holds its own numbered list) would otherwise run into the next item, and the
 * decoder, which finds an item's answer by its heading, could no longer read it.
 */
const toSingleLine = (text: string): string => text.replace(/\s*\n\s*/g, ' ').trim();

/** What the item is called: the question text, or the variable asked for. */
export const reviewItemLabel = (item: ReviewItem): string =>
  toSingleLine(
    item.kind === 'question' ? item.question.question : matchRequestedLabel(item.checkpoint)
  );

const itemKindLabel = (item: ReviewItem): string =>
  item.kind === 'question' ? 'Question' : 'Variable match';

const itemMarker = (index: number): string => `${index + 1}. `;

const itemHeading = (index: number, item: ReviewItem): string =>
  `${itemMarker(index)}${itemKindLabel(item)}: ${reviewItemLabel(item)}`;

/** Everything up to where the item's answer starts, indented under its marker. */
const answerPrefix = (index: number, item: ReviewItem): string =>
  `${itemHeading(index, item)}${ITEM_SEPARATOR}${' '.repeat(
    itemMarker(index).length
  )}${ANSWER_LABEL}: `;

const verdictFor = (item: ReviewItem, pick: ReviewPick | undefined): string => {
  if (item.kind === 'match') {
    return matchVerdict(pick?.type === 'use' || pick?.type === 'build' ? pick : undefined);
  }
  if (pick?.type === 'option') return pick.label;
  if (pick?.type === 'custom') return pick.text;
  return UNANSWERED_PHRASE;
};

export const buildReviewReply = (items: ReviewItem[], picks: Record<string, ReviewPick>): string =>
  [
    REPLY_PREAMBLE,
    ...items.map(
      (item, index) =>
        `${answerPrefix(index, item)}${toSingleLine(verdictFor(item, picks[item.id]))}`
    ),
  ].join(ITEM_SEPARATOR);

/** The answer on the item's line, or `''` when the reply has no such item. */
const readVerdict = (answerText: string | undefined, index: number, item: ReviewItem): string => {
  const text = answerText ?? '';
  const prefix = answerPrefix(index, item);
  const start = text.indexOf(prefix);
  if (start === -1) return '';
  const from = start + prefix.length;
  const end = text.indexOf('\n', from);
  return text.slice(from, end === -1 ? undefined : end);
};

export const parseReviewReply = (
  items: ReviewItem[],
  answerText: string | undefined
): Record<string, ReviewPick> => {
  const result: Record<string, ReviewPick> = {};
  items.forEach((item, index) => {
    const verdict = readVerdict(answerText, index, item);
    if (item.kind === 'match') {
      result[item.id] = parseMatchVerdict(verdict, item.checkpoint);
    } else if (verdict && verdict !== UNANSWERED_PHRASE) {
      result[item.id] = item.question.options.some((option) => option.label === verdict)
        ? { type: 'option', label: verdict }
        : { type: 'custom', text: verdict };
    }
  });
  return result;
};
